/** Known permission codes that map to app areas. Unknown codes never hide the whole menu. */
const AREA_CODES: Record<string, readonly string[]> = {
  inventory: [
    'view_inventory',
    'create_inventory',
    'edit_inventory',
    'delete_inventory',
    'inventory',
    'inventory_view',
    'inventory_edit',
  ],
  rental: [
    'view_equipment',
    'equipment',
    'rental',
    'reservations',
    'checkout',
    'checkin',
    'check_out',
    'check_in',
  ],
  maintenance: ['view_maintenance', 'maintenance', 'repair'],
  reports: ['view_reports', 'create_reports', 'reports', 'report'],
  settings: [
    'view_settings',
    'edit_settings',
    'settings',
    'manage_settings',
    'settings_view',
    'settings_edit',
  ],
  users: [
    'view_users',
    'create_users',
    'edit_users',
    'delete_users',
    'users',
    'user_permissions',
    'manage_users',
    'manage_roles',
  ],
};

const AREA_TOKENS: Record<string, readonly string[]> = {
  inventory: ['inventory'],
  rental: ['equipment', 'rental', 'reservation', 'checkout', 'checkin', 'check_out', 'check_in'],
  maintenance: ['maintenance', 'repair'],
  reports: ['report'],
  settings: ['setting'],
  users: ['user', 'role'],
};

const KNOWN_CODES = new Set(
  Object.values(AREA_CODES).flatMap((codes) => codes.map((code) => code.toLowerCase())),
);

export type NavArea = keyof typeof AREA_CODES;

export function normalizePermissionCodes(codes: readonly string[] | undefined): string[] {
  return (codes ?? [])
    .map((code) =>
      String(code)
        .trim()
        .toLowerCase()
        .replace(/[\s.\-]+/g, '_'),
    )
    .filter(Boolean);
}

export function isAdministrator(
  roles: readonly string[] | undefined,
  permissions: readonly string[] | undefined,
): boolean {
  const roleNames = (roles ?? []).map((role) => role.trim().toLowerCase());
  if (roleNames.some((role) => role === 'admin' || role === 'administrator' || role.includes('admin'))) {
    return true;
  }

  const codes = normalizePermissionCodes(permissions);
  return codes.includes('all') || codes.includes('*') || codes.includes('admin');
}

function grantsArea(code: string, area: NavArea): boolean {
  const allowed = AREA_CODES[area];
  if (allowed.includes(code)) {
    return true;
  }

  return AREA_TOKENS[area].some(
    (token) =>
      code === token ||
      code.startsWith(`${token}_`) ||
      code.endsWith(`_${token}`) ||
      code.includes(`_${token}_`) ||
      code.includes(token),
  );
}

/**
 * Hide a nav area only when the user has at least one recognisable permission
 * code AND none of those codes grant this area. Empty / unmapped seeds keep all links.
 */
export function canAccessNavArea(
  area: NavArea,
  roles: readonly string[] | undefined,
  permissions: readonly string[] | undefined,
): boolean {
  if (isAdministrator(roles, permissions)) {
    return true;
  }

  const codes = normalizePermissionCodes(permissions);
  if (!codes.length) {
    return true;
  }

  const hasKnownCode = codes.some(
    (code) => KNOWN_CODES.has(code) || Object.values(AREA_TOKENS).some((tokens) => tokens.some((token) => code.includes(token))),
  );
  if (!hasKnownCode) {
    return true;
  }

  return codes.some((code) => grantsArea(code, area));
}

export function canAccessPermission(code: string, permissions: readonly string[] | undefined, roles?: readonly string[]): boolean {
  if (isAdministrator(roles, permissions)) {
    return true;
  }

  const wanted = String(code)
    .trim()
    .toLowerCase()
    .replace(/[\s.\-]+/g, '_');
  if (!wanted) {
    return false;
  }

  return normalizePermissionCodes(permissions).includes(wanted);
}
